// A PÁCIENS belépője, nem a dolgozóké: a portál a `medical.patients` sorait hitelesíti.
import authService from '../../api/portal-auth';
import {loading, handleError, alert, storeLocal, destroyLocal, getLocal} from '@/state/generators/helpers';
import { clearSessionEnded } from '@/utils/session-state';
import { clearPatientToken } from '@/utils/apihub-patient-token';

export const state = {
    permissions: getLocal('permissions') || [],
    admin: getLocal('admin') || false,
    errors: {},
    loading: false,
    service: authService(),
    authenticated: !!getLocal('authenticated'),
    // kétfaktoros belépés: a jelszó rendben, a kódra várunk (a szerver állapota + szövegei), különben null
    twoFactor: null,
}

export const mutations = {
    'SET_AUTHENTICATED': function (state, boolean) {
        // belépés/kilépés: az apihub-token az előző pácienshez (vagy senkihez) tartozott
        clearPatientToken()
        state.authenticated = boolean

        // A portál belépése nem ad tokent (csak a pácienst), ezért a lejárt-munkamenet jelzőt a
        // belépett állapot beállása oldja fel, nem a válasz (l. api/axios.js, utils/session-state.js).
        if (boolean) {
            clearSessionEnded();
        }
    },

    'SET_TWO_FACTOR': function (state, challenge) {
        state.twoFactor = challenge
    },

    'SET_LOADING': function (state, boolean = true) {
        state.loading = boolean
    },

    'SET_ERRORS': function(state, errors) {
        state.errors = errors
    },

    'SET_ERROR': function(state, error) {
        const errors = Object.assign({}, state.errors)

        Object.keys(error).forEach(key => {
            errors[key]
                ? errors[key].push(error[key])
                : errors[key] = Array(error[key])
        })

        state.errors = errors
    },

    'SET_PERMISSIONS': function (state, permissions = []) {
        storeLocal('permissions', permissions)
        state.permissions = permissions
    },

    'SET_ADMIN': function (state, boolean) {
        storeLocal('admin', boolean)
        state.admin = boolean
    },
}

export const getters = {
    loading: function (state) {
        return !!state.loading
    },

    authenticated: function (state) {
        return state.authenticated
    },

    twoFactor: function (state) {
        return state.twoFactor
    },

    states: function (state) {
        const states = {};

        Object.keys(state.errors).forEach(key => {
                states[key] = false
            }
        )

        return states
    },

    errors: function (state) {
        const errors = Object.assign({}, (state.errors || {}))

        Object.keys(errors).forEach(key => {
            errors[key] = errors[key].join(' ')
        })

        return errors;
    },

    permissions: function (state) {
        return JSON.parse(JSON.stringify(state.permissions))
    },

    can: (state, getters, rootGetters) => (permission) => {
        if (permission === true) {
            return true;
        }

        if (rootGetters['user'].authenticated?.is_developer) {
            return true;
        }

        if (rootGetters['user'].authenticated?.is_admin && rootGetters['user'].authenticated?.is_global) {
            return true;
        }

        if (rootGetters['user'].authenticated?.is_admin && permission.startsWith('tenant')) {
            return true;
        }

        return getters.permissions.includes(permission);
    },

    admin: function (state) {
        return !!state.admin
    },
}

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    // eslint-disable-next-line no-unused-vars
    init({ dispatch }) {
        dispatch('validate')
    },

    addError: function ({commit}, payload) {
        commit('SET_ERROR', payload)
    },

    login: async function ({state, commit, dispatch, getters}, payload) {
        loading(commit)

        /*if (getters.authenticated) {
            dispatch('logout')
            /!*loading(commit, false)
            const message = this.$t('auth.logged_in').ucFirst()
            alert(commit, message, 'warning', 'warning')
            throw new Error(message)*!/
        }*/

        destroyLocal('accessToken')
        destroyLocal('permissions')
        destroyLocal('admin')
        commit('SET_PERMISSIONS', []);
        commit('SET_ADMIN', false)
        commit('SET_TWO_FACTOR', null)

        return state.service.csrf().then(() => {
            // Vendégként, foglalás közben nem szabad a teljes Vuex-állapotot resetelni:
            // a reset a portáloldalt is újramountolja, így elveszik a kiválasztott időpont.
            const prepareSession = getters.authenticated
                ? dispatch('logout').then(() => state.service.csrf())
                : Promise.resolve();

            return prepareSession.then(() => {
                    return state.service.login(payload).then(async (response) => {
                        // a host kétfaktoros belépést kér: a jelszó rendben, de még NINCS munkamenet;
                        // a hívó a kódképernyőt mutatja, a befejezést a `twoFactorVerify` végzi
                        if (response?.two_factor === true) {
                            commit('SET_TWO_FACTOR', response)
                            loading(commit, false);
                            return response;
                        }

                        await dispatch('completeSignIn');
                        loading(commit, false);
                        return response;
                    }).catch((e) => {
                        if (e.response?.status === 302) {
                            commit('SET_AUTHENTICATED', true)
                            storeLocal('authenticated', true)
                            dispatch('trans/setLocale', {}, {root: true})
                            dispatch('user/authenticated', {}, {root: true})
                            dispatch('app/configuration', {}, {root: true})
                            dispatch('permissions')
                            loading(commit, false)
                        } else {
                            handleError(e, commit)
                        }
                    })
            })
        })
    },

    /**
     * A sikeres belépés utáni közös rész (jelszó után, vagy a kétfaktoros kód után): nyelv, páciens,
     * beállítások, jogosultságok.
     */
    completeSignIn: async function ({commit, dispatch}) {
        commit('SET_AUTHENTICATED', true)
        storeLocal('authenticated', true);
        await Promise.all([
            dispatch('trans/setLocale', {}, {root: true}),
            dispatch('user/authenticated', {}, {root: true}),
            dispatch('app/configuration', {}, {root: true}),
            dispatch('permissions')
        ]);
    },

    /** Kód kérése (`mail` | `sms`); a hibát (várakozás, sikertelen küldés) a hívó jeleníti meg. */
    twoFactorSend: async function ({state, commit}, channel) {
        const challenge = await state.service.twoFactorSend(channel);
        commit('SET_TWO_FACTOR', Object.assign({}, state.twoFactor, challenge));
        return challenge;
    },

    /**
     * A kód ellenőrzése; jó kódnál bejelentkezve folytatja. A hibát a hívó jeleníti meg.
     *
     * A kódképernyő állapotát CSAK a teljes befejezés (páciens, menü, jogosultságok) UTÁN üríti: a hívó
     * erre lép tovább, és korábban a menü útvonalai még nem léteznének (dev2, 2026-10-29: 404).
     */
    twoFactorVerify: async function ({state, commit, dispatch}, code) {
        const response = await state.service.twoFactorVerify(code);
        await dispatch('completeSignIn');
        commit('SET_TWO_FACTOR', null);
        return response;
    },

    /** Vissza a jelszóhoz: a függő belépés a szerveren magától lejár. */
    cancelTwoFactor: function ({commit}) {
        commit('SET_TWO_FACTOR', null);
    },

    logout: function ({state, commit, dispatch}) {
        loading(commit);
        commit('SET_PERMISSIONS', []);
        commit('SET_ADMIN', false)

        return state.service.logout().then(response => {
            commit('SET_AUTHENTICATED', false);
            dispatch('user/setAuthenticated', {}, {root: true})
            destroyLocal('authenticated')
            destroyLocal('permissions')
            destroyLocal('admin')
            destroyLocal('configuration')
            this.reset()
            return response
        }).catch((e) => {
            commit('SET_AUTHENTICATED', false);
            dispatch('user/setAuthenticated', {}, {root: true})
            destroyLocal('authenticated')
            destroyLocal('permissions')
            destroyLocal('admin')
            destroyLocal('configuration')
            this.reset();
            if (e?.response?.status !== 401) {
                handleError(e, commit)
            }
        }).finally(() => {
            loading(commit, false);
        });
    },

    logoutOnlyFrontend: function ({commit, dispatch}) {
        commit('SET_AUTHENTICATED', false);
        dispatch('user/setAuthenticated', {}, {root: true});
        destroyLocal('authenticated');
        destroyLocal('permissions');
        destroyLocal('admin');
        destroyLocal('configuration');
        this.reset();
    },

    forgotPassword: function ({state, commit, getters, dispatch}, payload) {
        loading(commit)

        if (getters.authenticated) {
            loading(commit, false)
            const message = this.$t('auth.logged_in').ucFirst()
            alert(commit, message, 'warning', 'warning')
            throw new Error(message)
        }

        state.service.csrf().then(() => {
            return state.service.forgotPassword(payload).then((response) => {
                storeLocal('email', payload.email)
                loading(commit, false)
                dispatch('app/addAlert', {
                    message: response.message,
                    style: 'success',
                    icon: 'success',
                    countdown: 3000
                }, {root: true})
                return response.message
            }).catch((e) => {
                handleError(e, commit)
            })
        })
    },

    // ⚠️ A `resetPassword` és a `register` akció INNEN KIKERÜLT. Egyiket sem dispatch-elte
    // senki (megmérve), a regisztrációt pedig a `api/patient-registration.js` végzi, mert a
    // képernyőnek a cím-ellenőrzés is kell. Két kliens ugyanarra a végpontra előbb-utóbb
    // szétcsúszik; a jelszó-beállító képernyő ugyanezért hívja közvetlenül a sajátját.

    permissions: function ({commit, state}) {
        loading(commit)

        return state.service.permissions().then(response => {
            loading(commit, false);
            commit('SET_PERMISSIONS', response.data);
            if (response.additional && response.additional.admin) {
                commit('SET_ADMIN', response.additional.admin);
            }
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    // eslint-disable-next-line no-unused-vars
    validate({ getters, dispatch, rootGetters }) {
        if (!rootGetters['user/authenticated']) return Promise.resolve(null)
        if (!getters.permissions) {
            dispatch('permissions')
        }
        return rootGetters['user/authenticated'];
    },
}
